// Sets up OS-level push for this browser install — the half that reaches a
// closed app / locked phone with sound, complementary to socketClient.js
// (live tab only). See public/service-worker.js for the piece that actually
// receives the push and calls showNotification.
import axiosInstance from '../api/axiosConfig';

// PushSubscription keys arrive base64url-encoded; the Push API's `subscribe()`
// wants a raw Uint8Array. This is the standard conversion (MDN's own example) —
// there is no built-in browser API for it.
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

export function isPushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Full one-time setup: ask permission, subscribe, tell the backend. Safe to
 * call on every login — it's a no-op cost if the user already said yes and
 * already has a live subscription (the browser returns the existing one
 * rather than creating a duplicate).
 *
 * Returns silently (never throws) on anything short of success — permission
 * denied, no VAPID key configured server-side, browser doesn't support it at
 * all. This is a nice-to-have layered on top of email and the in-app socket
 * alert, never something that should be able to break login.
 */
export async function ensurePushSubscription() {
    if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

    try {
        const { data } = await axiosInstance.get('/api/realtime/vapid-public-key');
        if (!data?.publicKey) return { ok: false, reason: 'server-not-configured' };

        // Only ASKS if the user has never answered — re-prompting someone who
        // already said no is exactly the pattern that gets a site's whole
        // origin silently blocked by the browser.
        let permission = Notification.permission;
        if (permission === 'default') {
            permission = await Notification.requestPermission();
        }
        if (permission !== 'granted') return { ok: false, reason: `permission-${permission}` };

        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true, // required by Chrome: every push must show a visible notification
                applicationServerKey: urlBase64ToUint8Array(data.publicKey)
            });
        }

        await axiosInstance.post('/api/realtime/subscribe', { subscription: subscription.toJSON() });
        return { ok: true };
    } catch (error) {
        console.error('[pushClient] subscribe failed:', error);
        return { ok: false, reason: 'error', error };
    }
}

/** Called on logout so a shared/kiosk device stops ringing for the next user. */
export async function clearPushSubscription() {
    if (!isPushSupported()) return;
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) return;
        await axiosInstance.delete('/api/realtime/subscribe', { data: { endpoint: subscription.endpoint } }).catch(() => {});
        await subscription.unsubscribe();
    } catch (error) {
        console.error('[pushClient] unsubscribe failed:', error);
    }
}
